import { DataTableAuditCell } from '@frontend-platform/ui-lib';
import { Skeleton, Tooltip, TooltipContent, TooltipTrigger } from '@frontend-platform/ui-lib/ui';
import { formatRelativeTime } from '@frontend-platform/util-format';
import { Link } from '@tanstack/react-router';
import { ChevronRight, Info } from 'lucide-react';
import { type ReactNode, useState } from 'react';
import { ROUTES } from '@/config/routes';
import { useCalibrationEnvironments } from '../../hooks/use-calibration-environments';
import { useRunDetail } from '../../hooks/use-run-detail';
import { formatAbsoluteTime } from '../../lib/format';
import { BuildBadge } from '../build-badge';
import { AnalysisPanel } from './analysis-panel';
import { CriteriaPanel } from './criteria-panel';
import { RecommendationsPanel } from './recommendations-panel';
import { buildScopeLabel } from './utils';

interface RunDetailPageProps {
  runId: string;
}

interface SummaryBlock {
  key: string;
  label: string;
  value: string;
  node?: ReactNode;
  tooltip?: string;
}

export function RunDetailPage({ runId }: RunDetailPageProps) {
  const { data: runDetails, isLoading, isError, error } = useRunDetail(runId);
  const { getEnvLabel } = useCalibrationEnvironments();
  // Selecting a category clears the metric and vice versa — only one drill-down
  // is active at a time, matching RunDetails.vue's selectCategory/selectMetric.
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [selectedMetric, setSelectedMetric] = useState<string | null>(null);

  const selectCategory = (category: string) => {
    setSelectedCategory(category);
    setSelectedMetric(null);
  };
  const selectMetric = (metricId: string) => {
    setSelectedMetric(metricId);
    setSelectedCategory(null);
  };
  const clearSelection = () => {
    setSelectedCategory(null);
    setSelectedMetric(null);
  };

  const runName = runDetails?.runName ?? '';
  const hasSelection = !!selectedCategory || !!selectedMetric;

  const summaryBlocks: SummaryBlock[] = [
    {
      key: 'totalConversations',
      label: 'Total conversations',
      value: String(runDetails?.selectedConversationCount ?? '-'),
    },
    {
      key: 'env',
      label: 'Environment',
      value: getEnvLabel(runDetails?.evaluationEnvironment),
      node: (
        <span className="flex items-center gap-2">
          <span>{getEnvLabel(runDetails?.evaluationEnvironment)}</span>
          {runDetails ? <BuildBadge run={runDetails} /> : null}
        </span>
      ),
    },
    { key: 'scope', label: 'Scope', value: buildScopeLabel(runDetails, getEnvLabel) },
    {
      key: 'createdBy',
      label: 'Created By',
      value: runDetails?.createdBy?.name ?? '-',
      node: runDetails?.createdBy ? (
        <DataTableAuditCell
          relativeTime={formatRelativeTime(runDetails.createdAt)}
          title={`Created by ${runDetails.createdBy.name}`}
          updatedLabel="Created"
          updated={{
            when: formatAbsoluteTime(runDetails.createdAt),
            user: {
              displayName: runDetails.createdBy.name,
              avatarName: runDetails.createdBy.name,
              email: runDetails.createdBy.email,
              seed: runDetails.createdBy.id,
              rawUserId: runDetails.createdBy.id,
            },
          }}
        />
      ) : (
        <span className="text-foreground text-sm">-</span>
      ),
    },
  ];

  if (isError) {
    return (
      <div>
        <Link to={ROUTES.HOME} className="text-muted-foreground text-sm hover:text-foreground">
          ← Back to Calibration
        </Link>
        <div className="mt-10 text-center text-red-600">
          {error instanceof Error ? error.message : 'Failed to load run details'}
        </div>
      </div>
    );
  }

  // No padding here: RootLayout's <main> already pads the page. Adding p-6 on
  // top double-padded this route relative to every other one — the run list
  // deliberately carries no padding of its own for the same reason.
  return (
    <div className="flex h-full flex-col gap-6 overflow-hidden">
      {/* Header: breadcrumb + run name */}
      <div className="flex flex-col gap-2.5">
        <div className="flex items-center gap-2.5 text-sm">
          <Link to={ROUTES.HOME} className="text-muted-foreground hover:text-foreground">
            Calibration
          </Link>
          <ChevronRight className="h-3.5 w-3.5 text-muted-foreground" />
          {isLoading ? (
            <Skeleton className="h-4 w-32" />
          ) : (
            <span className="text-foreground">{runName}</span>
          )}
        </div>
        {isLoading ? (
          <Skeleton className="h-7 w-64" />
        ) : (
          <h1 className="font-medium text-2xl text-foreground">{runName}</h1>
        )}
      </div>

      {/* Summary blocks */}
      <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
        {summaryBlocks.map((block) => (
          <div
            key={block.key}
            className="flex flex-col gap-2 rounded-lg border border-border bg-background p-6"
          >
            <div className="flex items-center gap-1 font-medium text-muted-foreground text-sm">
              {block.label}
              {block.tooltip && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Info className="h-3 w-3 cursor-pointer text-muted-foreground" />
                  </TooltipTrigger>
                  <TooltipContent>{block.tooltip}</TooltipContent>
                </Tooltip>
              )}
            </div>
            <div className="font-semibold text-base text-foreground">
              {isLoading ? <Skeleton className="h-4 w-24" /> : (block.node ?? block.value)}
            </div>
          </div>
        ))}
      </div>

      {/* Left criteria panel + right analysis/recommendations */}
      <div className="flex min-h-0 flex-1 gap-4 overflow-hidden">
        <div className="w-2/5 overflow-auto">
          <CriteriaPanel
            runDetails={runDetails}
            selectedCategory={selectedCategory}
            selectedMetric={selectedMetric}
            onSelectCategory={selectCategory}
            onSelectMetric={selectMetric}
            onClear={clearSelection}
          />
        </div>
        <div className="min-h-0 flex-1 overflow-hidden">
          {hasSelection ? (
            <AnalysisPanel
              runDetails={runDetails}
              selectedCategory={selectedCategory}
              selectedMetric={selectedMetric}
            />
          ) : (
            <RecommendationsPanel recommendations={runDetails?.items ?? []} />
          )}
        </div>
      </div>
    </div>
  );
}
